import React, { useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import { MESSAGES } from "../utils/cardBodyConstants.js";

/**
 * ActionsDropdown Component
 * Reusable dropdown menu for duplicate and delete actions.
 *
 * Rendered via a portal into document.body and positioned with `position: fixed`,
 * computed from `anchorEl`'s bounding rect. This keeps the popup outside any
 * scrollable/overflow ancestor of the row it belongs to — it never gets clipped
 * and never inflates that row's scrollWidth (which previously showed an
 * unwanted horizontal scrollbar under the row once the menu was correctly
 * position-scoped to it).
 */
const ActionsDropdown = ({
  isOpen,
  activeId,
  itemId,
  duplicateLoading,
  isDeleteLoading,
  deleteId,
  onDuplicate,
  onDelete,
  onMouseDown,
  anchorEl,
}) => {
  const [coords, setCoords] = useState(null);
  const open = isOpen && activeId === itemId;

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      // Hidden sibling (display:none desktop/mobile twin) must not portal a menu
      if (!rect.width && !rect.height) {
        setCoords(null);
        return;
      }
      setCoords({
        top: rect.bottom + 13, // matches the original margin-top: 13px gap under the toggle
        right: window.innerWidth - rect.right - 34, // matches the original right: -34px offset
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorEl]);

  if (!open || !coords) {
    return null;
  }

  return ReactDOM.createPortal(
    <ul
      className="dropdown-menu1"
      style={{ position: "fixed", top: coords.top, right: coords.right, marginTop: 0 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={onMouseDown}
    >
      <li
        onClick={duplicateLoading ? undefined : onDuplicate}
        onMouseDown={onMouseDown}
        style={{
          pointerEvents: duplicateLoading ? "none" : "auto",
          cursor: duplicateLoading ? "default" : "pointer"
        }}
      >
        <span>
          {duplicateLoading ? (
            <div
              style={{
                width: "80px",
                height: "18px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          ) : (
            <span>{MESSAGES.DUPLICATE}</span>
          )}
        </span>
      </li>
      <li
        onClick={isDeleteLoading && itemId === deleteId ? undefined : onDelete}
        onMouseDown={onMouseDown}
        style={{
          pointerEvents: isDeleteLoading && itemId === deleteId ? "none" : "auto",
          cursor: isDeleteLoading && itemId === deleteId ? "default" : "pointer"
        }}
      >
        {itemId === deleteId && isDeleteLoading ? (
          <div
            style={{
              width: "60px",
              height: "18px",
              backgroundColor: "#e5e7eb",
              borderRadius: "4px",
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        ) : (
          <span>{MESSAGES.DELETE}</span>
        )}
      </li>
    </ul>,
    document.body
  );
};

export default ActionsDropdown;
